'use strict';


import { BaseElement } from '../../interfaces/BaseElement';
import { isDesktop, settingsData, threeJsVariables, TRoot } from '../../../settings';
import { Box2, Vector2, Vector3 } from 'three';
import { getCanvasWidthDiff } from '../../../app';

export class PointViewManager {
	static scope = null;
	
	constructor () {
		this.points = [];
		this.bounds = [];
		this.container = null;
		this.bmbyMan = null;
		this.backBtn = null;
		this.draggableElement = null;
		
		this.activePoint = null;
		this.portalsIsOpened = false;
		
		this.dragStart = false;
		this.dragMove = false;
		
		this.targetPoint = null;
		
		this.coords = {
			down: new Vector2(),
			move: new Vector2(),
			up: new Vector2()
		};
		
		this.onPointerMove = null;
		this.onPointerUp = null;
		
		PointViewManager.scope = this;
	}
	
	add (point) {
		point.label.element.addEventListener('pointerenter',(e) => this.#onPointEnter(e,point));
		point.label.element.addEventListener('pointerleave',(e) => this.#onPointLeave(e,point));
		point.label.element.addEventListener('click', (e) => this.#onClicked(e, point));
		this.points.push(point);
	}
	
	#getBounds() {
		if (settingsData.isTouchEvent) {
			this.bounds = [];
			setTimeout(() => {
				for (let i = 0; i < this.points.length; i++) {
					const bounds = this.points[i].label.element.getBoundingClientRect();
					const bbox = new Box2().setFromCenterAndSize(new Vector2(bounds.x + bounds.width / 2,bounds.y + bounds.height / 2),new Vector2(bounds.width,bounds.height));
					this.bounds.push({
						point: this.points[i],
						bbox
					});
				}
			},300);
		}
	}
	
	#onPointEnter(e,point) {
		if (this.dragStart && this.dragMove) {
			this.targetPoint = point;
		}
	}
	
	#onPointLeave() {
		if (this.dragStart && this.dragMove) {
			this.targetPoint = null;
		}
	}
	
	#onPointerMove(e) {
		this.coords.move.set(e.pageX,e.pageY);
		const dist = Math.hypot(this.coords.down.x - this.coords.move.x,this.coords.down.y - this.coords.move.y);
		
		if (dist > 5) {
			this.draggableElement.style.top = e.pageY + 'px';
			this.draggableElement.style.left = e.pageX + 'px';
			
			if (this.draggableElement.parentElement === null) {
				this.#onDragStart();
			}
			
			if (settingsData.isTouchEvent && this.bounds.length) {
				this.#onPointLeave();
				
				for (let i = 0; i < this.bounds.length; i++) {
					const pointData = this.bounds[i];
					if (pointData.bbox.containsPoint(this.coords.move)) {
						this.#onPointEnter(null,pointData.point);
						break;
					}
				}
			}
		}
	}
	
	#onPointDown(e) {
		this.dragStart = true;
		this.coords.down.set(Math.floor(e.pageX),Math.floor(e.pageY));
		
		document.body.addEventListener('pointermove',this.onPointerMove);
		document.body.addEventListener('pointerup',this.onPointerUp);
	}
	
	#onPointUp(e) {
		this.coords.up.set(Math.floor(e.pageX),Math.floor(e.pageY));
		
		if (this.targetPoint) {
			this.#onDrop();
		}
		
		if (this.coords.down.equals(this.coords.up)) {
			this.togglePortals();
		} else {
			this.#onDragEnd();
		}
		
		document.body.removeEventListener('pointermove',this.onPointerMove);
		document.body.removeEventListener('pointerup',this.onPointerUp);
	}
	
	init (parent) {
		if (this.points.length === 0)
			return;
		
		this.onPointerMove = this.#onPointerMove.bind(this);
		this.onPointerUp = this.#onPointUp.bind(this);
		
		this.container = new BaseElement()
			.addClass('point-view-manager', isDesktop ? null : 'background-backdrop-filter')
			.addAttribute('data-state', 'man')
			.appendTo(parent)
			.DOMElement;
		
		this.draggableElement = new BaseElement().DOMElement;
		this.draggableElement.style.opacity = 0.8;
		this.draggableElement.style.width = '40px';
		this.draggableElement.style.height = '40px';
		this.draggableElement.style.fontSize = '30px';
		this.draggableElement.style.background = 'rgba(0,0,0,0.8)';
		this.draggableElement.style.borderRadius = '50%';
		this.draggableElement.style.display = 'flex';
		this.draggableElement.style.justifyContent = 'center';
		this.draggableElement.style.alignItems = 'center';
		this.draggableElement.style.position = 'absolute';
		this.draggableElement.style.top = '-9999px';
		this.draggableElement.style.pointerEvents = 'none';
		this.draggableElement.style.touchAction = 'none';
		this.draggableElement.style.userSelect = 'none';
		this.draggableElement.style.zIndex = 999999999;
		this.draggableElement.style.transform = 'translate(-50%,-50%)';
		
		this.draggableElement.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="30px" height="33px" viewBox="0 0 30 33" version="1.1">
<g id="surface1">
<path style=" stroke:none;fill-rule:nonzero;fill:rgb(255%,255%,255%);fill-opacity:1;" d="M 23.824219 23.441406 C 22.726562 23.121094 22.273438 24.78125 23.371094 25.058594 C 26.320312 25.867188 27.640625 26.964844 27.640625 27.691406 C 27.640625 29.171875 22.695312 31.324219 14.945312 31.324219 C 7.195312 31.324219 2.25 29.171875 2.25 27.691406 C 2.25 26.964844 3.566406 25.867188 6.515625 25.058594 C 6.988281 24.945312 7.269531 24.453125 7.125 23.996094 C 6.988281 23.554688 6.511719 23.304688 6.066406 23.441406 C 2.511719 24.417969 0.554688 25.925781 0.554688 27.691406 C 0.554688 31.175781 7.792969 33 14.945312 33 C 22.097656 33 29.335938 31.175781 29.335938 27.691406 C 29.335938 25.925781 27.375 24.417969 23.824219 23.441406 Z M 23.824219 23.441406 "/>
<path style=" stroke:none;fill-rule:nonzero;fill:rgb(255%,255%,255%);fill-opacity:1;" d="M 8.375 21.792969 L 10.445312 21.792969 L 11.3125 26.136719 C 11.496094 27.984375 13.066406 29.394531 14.945312 29.390625 C 16.796875 29.40625 18.359375 28.035156 18.566406 26.214844 L 19.449219 21.792969 L 21.515625 21.792969 C 22.945312 21.789062 24.105469 20.644531 24.109375 19.226562 L 24.109375 16.609375 C 24.175781 13.160156 21.546875 10.175781 18.226562 9.382812 C 23.015625 5.585938 18.539062 -1.921875 12.832031 0.457031 C 12.402344 0.644531 12.207031 1.136719 12.394531 1.5625 C 12.925781 2.628906 14.074219 1.574219 14.933594 1.703125 C 19.6875 1.820312 19.6875 8.726562 14.933594 8.84375 C 12.628906 8.871094 10.867188 6.632812 11.429688 4.425781 C 11.539062 3.976562 11.257812 3.523438 10.800781 3.417969 C 9.730469 3.207031 9.640625 4.519531 9.640625 5.273438 C 9.640625 6.945312 10.441406 8.414062 11.667969 9.378906 C 8.347656 10.167969 5.714844 13.152344 5.777344 16.605469 L 5.777344 19.21875 C 5.777344 20.640625 6.941406 21.792969 8.375 21.792969 Z M 7.472656 16.605469 C 7.523438 8.535156 22.367188 8.535156 22.417969 16.605469 L 22.417969 19.21875 C 22.417969 19.714844 22.015625 20.113281 21.515625 20.113281 L 19.601562 20.113281 L 19.601562 17.996094 C 19.59375 16.902344 17.914062 16.898438 17.90625 17.996094 L 17.90625 20.871094 L 16.890625 25.96875 C 16.640625 28.316406 13.160156 28.234375 12.984375 25.890625 L 11.988281 20.871094 L 11.988281 17.996094 C 11.984375 16.902344 10.296875 16.898438 10.292969 17.996094 L 10.292969 20.113281 L 8.378906 20.113281 C 7.878906 20.113281 7.472656 19.714844 7.472656 19.21875 Z M 7.472656 16.605469 "/>
</g>
</svg>`;
		
		this.bmbyMan = new BaseElement()
			.addClass('point-view-manager__bmby-man', 'icon-street-view')
			.event('pointerdown',this.#onPointDown.bind(this))
			.appendTo(this.container)
			.DOMElement;
		
		this.backBtn = new BaseElement()
			.addClass('point-view-manager__back-btn', 'icon-back', 'hidden')
			.event('click', this.backFromView.bind(this))
			.appendTo(this.container)
			.DOMElement;
		
	}
	
	backFromView () {
		if (this.activePoint) {
			this.activePoint.viewOut();
			this.swap(false);
			this.activePoint = null;
		}
	}
	
	
	togglePortals() {
		this.portalsIsOpened = !this.portalsIsOpened;
		
		for (let i = 0; i < this.points.length; i++) {
			this.points[i].show(this.portalsIsOpened);
		}
	}
	
	#onClicked(e,point) {
		this.swap(true);
		this.activePoint = point;
		this.activePoint.viewIn();
		this.togglePortals();
	}
	
	swap (state) {
		if (state) {
			this.bmbyMan.classList.add('hidden');
			this.backBtn.classList.remove('hidden');
			this.container.setAttribute('data-state', 'back');
		} else {
			this.bmbyMan.classList.remove('hidden');
			this.backBtn.classList.add('hidden');
			this.container.setAttribute('data-state', 'man');
		}
	}
	
	#onDrop () {
		this.swap(true);
		this.activePoint = this.targetPoint;
		this.activePoint.viewIn();
	}
	
	#onDragEnd () {
		this.draggableElement && this.draggableElement.remove();
		this.bmbyMan.style.pointerEvents = '';
		this.bmbyMan.style.touchAction = '';
		this.bmbyMan.style.userSelect = '';
		this.bmbyMan.style.opacity = 1;
		this.dragStart = false;
		this.dragMove = false;
		this.targetPoint = null;
		this.bounds = [];
		document.body.classList.remove('draggable');
		threeJsVariables.renderer.domElement.style.cursor = 'default';
		
		for (let i = 0; i < this.points.length; i++) {
			this.points[i].show(false);
		}
		
		this.portalsIsOpened = false;
		
		if (!this.activePoint) {
			this.swap(false);
		}
	}
	
	#onDragStart () {
		for (let i = 0; i < this.points.length; i++) {
			this.points[i].show(true);
		}
		
		this.#getBounds();
		this.bmbyMan.style.pointerEvents = 'none';
		this.bmbyMan.style.touchAction = 'none';
		this.bmbyMan.style.userSelect = 'none';
		this.dragMove = true;
		this.portalsIsOpened = true;
		this.bmbyMan.style.opacity = 0;
		document.body.appendChild(this.draggableElement);
		document.body.classList.add('draggable');
	}
	
	reset () {
		this.swap(false);
		if (this.activePoint) {
			this.activePoint.visibilityToggle(true);
			this.activePoint.restoreControlsSettings();
			this.activePoint = null;
		}
	}
	
	closePortals() {
		if (this.portalsIsOpened) {
			this.togglePortals();
		}
	}
	
	static closePortals() {
		PointViewManager.scope && PointViewManager.scope.closePortals();
	}
	
	static init(parent) {
		PointViewManager.scope && PointViewManager.scope.init(parent);
	}
	
	static resetState () {
		PointViewManager.scope && PointViewManager.scope.reset();
	}
}

